import { useState, useEffect } from 'react';
import type { Harvest, Season } from '../../../../types';
import { getFarmersByHtx, getFieldsByFarmer, getSeasonsByFarmer, getContractsByFarmer } from '../../../../store';
import { todayInputValue } from '../../../../utils/format';
import { showToast } from '../../../../components/base/Toast';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { useAuth } from '../../../../hooks/useAuth';
import { useCategories } from '../../../../hooks/useCategories';
import { UNIT_OF_MEASURE_FALLBACK } from '../../../../constants/categoryFallbacks';
import { CropSelect } from '@/components/feature/CropSelect';

interface HarvestFormModalProps {
  editItem: Harvest | null;
  onClose: () => void;
  onSave: (data: Omit<Harvest, 'id' | 'maLanThuHoach'>) => void;
}

export default function HarvestFormModal({ editItem, onClose, onSave }: HarvestFormModalProps) {
  useModalBackHandler(true, onClose);
  const { user } = useAuth();
  const { names: unitOptions } = useCategories('UNIT_OF_MEASURE', UNIT_OF_MEASURE_FALLBACK);
  const farmers = getFarmersByHtx(user?.htxId);
  const [selectedFarmer, setSelectedFarmer] = useState(editItem?.farmerId || farmers[0]?.id || '');
  const fields = getFieldsByFarmer(selectedFarmer);
  const seasons = getSeasonsByFarmer(selectedFarmer);
  const farmerContracts = getContractsByFarmer(selectedFarmer);

  const [form, setForm] = useState({
    farmerId: editItem?.farmerId || farmers[0]?.id || '',
    fieldId: editItem?.fieldId || '',
    seasonId: editItem?.seasonId || '',
    contractId: editItem?.contractId || '',
    cayTrong: editItem?.cayTrong || '',
    loaiSanPham: editItem?.loaiSanPham || '',
    soLuong: editItem ? String(editItem.soLuong) : '',
    donVi: editItem?.donVi || 'kg',
    ngayDuKien: editItem?.ngayDuKien || todayInputValue(),
    ngayThuHoach: editItem?.ngayThuHoach || '',
    status: editItem?.status || 'Dự kiến' as Harvest['status'],
    giaUocTinh: editItem ? String(editItem.giaUocTinh) : '',
    chatLuong: editItem?.chatLuong || '',
    ghiChu: editItem?.ghiChu || '',
  });

  const selectedSeason = seasons.find((s: Season) => s.id === form.seasonId);

  const fillFromSeason = (seasonId: string) => {
    const s = seasons.find((se: Season) => se.id === seasonId);
    if (!s) return;
    // Auto-link the offtake contract this season falls under, if there's
    // exactly one candidate — lets the harvest reconcile against the
    // contract's own expected output/price/delivery date later. The HTX can
    // still override via the picker below.
    const matchingContract = farmerContracts.find((c) => c.seasonIds?.includes(seasonId));
    setForm((prev) => ({
      ...prev,
      seasonId,
      fieldId: s.fieldId,
      cayTrong: s.cayTrong,
      loaiSanPham: s.cayTrong,
      // Carry the season's figure across with the unit it was actually entered
      // in. The old code did `expectedYield * 1000` and forced donVi to 'kg',
      // which only held if the season happened to be in tấn — a season recorded
      // as 5.500 kg was prefilled as 5.500.000 kg.
      soLuong: s.expectedYield ? String(s.expectedYield) : prev.soLuong,
      donVi: s.unit || prev.donVi,
      ngayDuKien: s.endDate || prev.ngayDuKien,
      contractId: matchingContract?.id || prev.contractId,
    }));
  };

  useEffect(() => {
    if (!editItem && seasons.length > 0 && !form.seasonId) {
      fillFromSeason(seasons[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = () => {
    const soLuong = Number(form.soLuong);
    const giaUocTinh = Number(form.giaUocTinh);
    if (!form.farmerId || !form.seasonId || !form.cayTrong.trim() || !soLuong || !form.ngayDuKien || !giaUocTinh) {
      showToast('Vui lòng điền đầy đủ thông tin bắt buộc', 'warning');
      return;
    }
    onSave({
      farmerId: form.farmerId,
      fieldId: form.fieldId || undefined,
      seasonId: form.seasonId,
      contractId: form.contractId || undefined,
      cayTrong: form.cayTrong,
      loaiSanPham: form.loaiSanPham || form.cayTrong,
      soLuong,
      donVi: form.donVi,
      ngayDuKien: form.ngayDuKien,
      ngayThuHoach: form.ngayThuHoach || undefined,
      status: form.status,
      giaUocTinh,
      chatLuong: form.chatLuong || undefined,
      ghiChu: form.ghiChu || undefined,
    });
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
          <h3 className="text-lg font-semibold text-slate-900">
            {editItem ? 'Sửa lần thu hoạch' : 'Tạo lần thu hoạch mới'}
          </h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="px-4 sm:px-6 py-4 space-y-4">
          {/* Nông hộ */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nông hộ <span className="text-red-500">*</span></label>
            <select
              value={form.farmerId}
              onChange={(e) => {
                const id = e.target.value;
                setSelectedFarmer(id);
                setForm({ ...form, farmerId: id, seasonId: '', fieldId: '', cayTrong: '', loaiSanPham: '' });
              }}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
            >
              {farmers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>

          {/* Mùa vụ + Tự động fill */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mùa vụ <span className="text-red-500">*</span></label>
            <select
              value={form.seasonId}
              onChange={(e) => fillFromSeason(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
            >
              <option value="">-- Chọn mùa vụ --</option>
              {seasons.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.cayTrong})</option>)}
            </select>
          </div>

          {/* Hợp đồng liên quan (bao tiêu) — để đối chiếu sản lượng/giá thực tế với hợp đồng */}
          {farmerContracts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Hợp đồng liên quan</label>
              <select
                value={form.contractId}
                onChange={(e) => setForm({ ...form, contractId: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              >
                <option value="">-- Không gắn hợp đồng --</option>
                {farmerContracts.map((c) => <option key={c.id} value={c.id}>{c.soHopDong}{c.cayTrong ? ` (${c.cayTrong})` : ''}</option>)}
              </select>
            </div>
          )}

          {/* Thửa đất (auto-fill, có thể chỉnh) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Thửa đất</label>
            <select
              value={form.fieldId}
              onChange={(e) => setForm({ ...form, fieldId: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
            >
              {fields.map((fi) => <option key={fi.id} value={fi.id}>{fi.name}</option>)}
            </select>
          </div>

          {/* Cây trồng */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cây trồng <span className="text-red-500">*</span></label>
            <CropSelect
              value={form.cayTrong}
              onChange={(name) => setForm({ ...form, cayTrong: name })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-agrida-500"
            />
          </div>

          {/* Loại sản phẩm */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Loại sản phẩm</label>
            <input
              type="text"
              value={form.loaiSanPham}
              onChange={(e) => setForm({ ...form, loaiSanPham: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              placeholder="Lúa tươi ĐT12"
            />
          </div>

          {/* Sản lượng dự kiến */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sản lượng dự kiến <span className="text-red-500">*</span></label>
              <input
                type="number"
                value={form.soLuong}
                onChange={(e) => setForm({ ...form, soLuong: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                placeholder="8500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Đơn vị</label>
              <input
                type="text"
                list="harvest-unit-of-measure-options"
                value={form.donVi}
                onChange={(e) => setForm({ ...form, donVi: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                placeholder="kg"
              />
              <datalist id="harvest-unit-of-measure-options">
                {unitOptions.map((u) => (<option key={u} value={u} />))}
              </datalist>
            </div>
          </div>

          {/* Giá ước tính */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Giá ước tính (VND) <span className="text-red-500">*</span></label>
            <input
              type="number"
              value={form.giaUocTinh}
              onChange={(e) => setForm({ ...form, giaUocTinh: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              placeholder="8500"
            />
          </div>

          {/* Ngày dự kiến + thực tế */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ngày dự kiến <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={form.ngayDuKien}
                onChange={(e) => setForm({ ...form, ngayDuKien: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ngày thực tế</label>
              <input
                type="date"
                value={form.ngayThuHoach}
                onChange={(e) => setForm({ ...form, ngayThuHoach: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              />
            </div>
          </div>

          {/* Chất lượng */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Chất lượng dự kiến</label>
            <select
              value={form.chatLuong}
              onChange={(e) => setForm({ ...form, chatLuong: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
            >
              <option value="">-- Chọn --</option>
              <option value="Tốt">Tốt</option>
              <option value="Đạt">Đạt</option>
              <option value="Trung bình">Trung bình</option>
              <option value="Kém">Kém</option>
            </select>
          </div>

          {/* Trạng thái */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Trạng thái</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as Harvest['status'] })}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
            >
              <option value="Dự kiến">Dự kiến</option>
              <option value="Nông hộ đã cập nhật">Nông hộ đã cập nhật</option>
              <option value="Chờ HTX xác nhận">Chờ HTX xác nhận</option>
              <option value="HTX đã xác nhận">HTX đã xác nhận</option>
              <option value="Sản lượng thấp">Sản lượng thấp</option>
              <option value="Hoàn thành">Hoàn thành</option>
              <option value="Hủy">Hủy</option>
            </select>
          </div>

          {/* Ghi chú */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú</label>
            <textarea
              value={form.ghiChu}
              onChange={(e) => setForm({ ...form, ghiChu: e.target.value })}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              placeholder="Ghi chú về đợt thu hoạch..."
            />
            <p className="text-xs text-slate-400 mt-1">{form.ghiChu.length}/500</p>
          </div>

          {/* Info box from season */}
          {selectedSeason && (
            <div className="bg-slate-50 rounded-xl p-3 space-y-1 text-sm">
              <p className="font-medium text-slate-700">Thông tin từ mùa vụ:</p>
              <p className="text-xs text-slate-500">Thửa đất: {fields.find((f) => f.id === selectedSeason.fieldId)?.name || '-'}</p>
              <p className="text-xs text-slate-500">Cây trồng: {selectedSeason.cayTrong} ({selectedSeason.variety})</p>
              <p className="text-xs text-slate-500">Sản lượng dự kiến: {selectedSeason.expectedYield} {selectedSeason.unit}</p>
              <p className="text-xs text-slate-500">Ngày kết thúc: {selectedSeason.endDate || 'Chưa có'}</p>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 sm:px-6 py-3 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2.5 rounded-md text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 transition-colors whitespace-nowrap min-h-[44px]"
          >
            {editItem ? 'Lưu thay đổi' : 'Tạo lần thu hoạch'}
          </button>
        </div>
      </div>
    </div>
  );
}